import { format } from "date-fns";
import { CalendarIcon, Check, ChevronLeft, Clock } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface DateTimeRange {
  start?: string;
  end?: string;
}

interface PresetOption {
  value: string;
  label: string;
}

const presetOptions: PresetOption[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "Past week" },
  { value: "month", label: "Past month" },
  { value: "quarter", label: "Past 3 months" },
  { value: "year", label: "Past year" },
];

interface DateTimeRangeFilterProps {
  value: DateTimeRange | null;
  onChange: (range: DateTimeRange | null) => void;
  placeholder?: string;
  showPresets?: boolean;
  showTime?: boolean;
}

const convertTo24Hour = (hour12: number, isPM: boolean): number => {
  if (hour12 === 12) {
    return isPM ? 12 : 0;
  }
  return isPM ? hour12 + 12 : hour12;
};

const convertTo12Hour = (hour24: number): { hour: number; isPM: boolean } => {
  if (hour24 === 0) {
    return { hour: 12, isPM: false };
  }
  if (hour24 === 12) {
    return { hour: 12, isPM: true };
  }
  if (hour24 > 12) {
    return { hour: hour24 - 12, isPM: true };
  }
  return { hour: hour24, isPM: false };
};

interface TimeInputProps {
  time: { hour: number; minute: number; isPM: boolean };
  onHourChange: (hour: number) => void;
  onMinuteChange: (minute: number) => void;
  onPeriodChange: (isPM: boolean) => void;
}

const TimeInput: React.FC<TimeInputProps> = ({
  time,
  onHourChange,
  onMinuteChange,
  onPeriodChange,
}) => {
  const handleHourChange = (value: string) => {
    const hour = Number(value);
    if (!isNaN(hour) && hour >= 1 && hour <= 12) {
      onHourChange(hour);
    }
  };

  const handleMinuteChange = (value: string) => {
    const minute = Number(value);
    if (!isNaN(minute) && minute >= 0 && minute <= 59) {
      onMinuteChange(minute);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min="1"
        max="12"
        value={time.hour}
        onChange={e => handleHourChange(e.target.value)}
        className="h-8 w-12 border-input text-center text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <span className="text-muted-foreground">:</span>
      <Input
        type="number"
        min="0"
        max="59"
        value={time.minute.toString().padStart(2, "0")}
        onChange={e => handleMinuteChange(e.target.value)}
        className="h-8 w-12 border-input text-center text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <div className="ml-1 flex overflow-hidden rounded-md border border-input bg-background">
        <Button
          onClick={() => onPeriodChange(false)}
          variant="ghost"
          className={`h-8 rounded-none px-2.5 text-xs ${
            !time.isPM
              ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
              : "bg-background hover:bg-accent hover:text-accent-foreground"
          }`}
        >
          AM
        </Button>
        <Button
          onClick={() => onPeriodChange(true)}
          variant="ghost"
          className={`h-8 rounded-none border-l border-input px-2.5 text-xs ${
            time.isPM
              ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
              : "bg-background hover:bg-accent hover:text-accent-foreground"
          }`}
        >
          PM
        </Button>
      </div>
    </div>
  );
};

type ViewMode = "presets" | "custom-start" | "custom-end";

const DateTimeRangeFilter: React.FC<DateTimeRangeFilterProps> = ({
  value,
  onChange,
  placeholder = "Date range",
  showPresets = true,
  showTime = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("presets");
  const [displayMonth, setDisplayMonth] = useState<Date>(new Date());

  const startDate = useMemo(() => (value?.start ? new Date(value.start) : undefined), [value]);
  const endDate = useMemo(() => (value?.end ? new Date(value.end) : undefined), [value]);

  const calculatePresetRange = useCallback((preset: string): DateTimeRange | null => {
    const now = new Date();
    let start: Date | undefined;

    switch (preset) {
      case "today":
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "week":
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "month":
        start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case "quarter":
        start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case "year":
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        return null;
    }

    return start ? { start: start.toISOString(), end: now.toISOString() } : null;
  }, []);

  const getCurrentPreset = useCallback((): string | null => {
    if (!value?.start) {
      return null;
    }

    const now = new Date();
    const start = new Date(value.start);
    const today = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const quarterAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

    const isSameDay = (date1: Date, date2: Date) =>
      Math.abs(date1.getTime() - date2.getTime()) < 24 * 60 * 60 * 1000;

    if (isSameDay(start, today)) {
      return "today";
    }
    if (isSameDay(start, weekAgo)) {
      return "week";
    }
    if (isSameDay(start, monthAgo)) {
      return "month";
    }
    if (isSameDay(start, quarterAgo)) {
      return "quarter";
    }
    if (isSameDay(start, yearAgo)) {
      return "year";
    }

    return null;
  }, [value]);

  const isCustomRange = value !== null && getCurrentPreset() === null;

  const handlePresetSelect = (preset: string) => {
    const range = calculatePresetRange(preset);
    onChange(range);
    setIsOpen(false);
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) {
      return;
    }

    const newDate = new Date(date);
    if (startDate) {
      newDate.setHours(startDate.getHours(), startDate.getMinutes());
    } else {
      newDate.setHours(0, 0, 0, 0);
    }

    onChange({
      start: newDate.toISOString(),
      end: value?.end,
    });
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (!date) {
      return;
    }

    const newDate = new Date(date);
    if (endDate) {
      newDate.setHours(endDate.getHours(), endDate.getMinutes());
    } else {
      newDate.setHours(23, 59, 59, 999);
    }

    onChange({
      start: value?.start,
      end: newDate.toISOString(),
    });
  };

  const handleStartTimeChange = (hour: number, minute: number, isPM: boolean) => {
    const date = startDate || new Date();
    const newDate = new Date(date);
    const hour24 = convertTo24Hour(hour, isPM);
    newDate.setHours(hour24, minute);

    onChange({
      start: newDate.toISOString(),
      end: value?.end,
    });
  };

  const handleEndTimeChange = (hour: number, minute: number, isPM: boolean) => {
    const date = endDate || new Date();
    const newDate = new Date(date);
    const hour24 = convertTo24Hour(hour, isPM);
    newDate.setHours(hour24, minute);

    onChange({
      start: value?.start,
      end: newDate.toISOString(),
    });
  };

  const handleClear = () => {
    onChange(null);
    setViewMode("presets");
    setIsOpen(false);
  };

  const handleOpenCustom = () => {
    setViewMode("custom-start");
    if (startDate) {
      setDisplayMonth(startDate);
    } else {
      setDisplayMonth(new Date());
    }
  };

  const handleBackToPresets = () => {
    setViewMode("presets");
  };

  const isValidRange = () => {
    if (startDate && endDate) {
      return startDate <= endDate;
    }
    return true;
  };

  const getDisplayValue = (): string => {
    const currentPreset = getCurrentPreset();
    if (currentPreset) {
      const option = presetOptions.find(opt => opt.value === currentPreset);
      return option?.label ?? placeholder;
    }

    if (startDate && endDate) {
      if (showTime) {
        return `${format(startDate, "MMM d, h:mm a")} → ${format(endDate, "MMM d, h:mm a")}`;
      }
      return `${format(startDate, "MMM d")} → ${format(endDate, "MMM d")}`;
    }

    if (startDate) {
      if (showTime) {
        return `From ${format(startDate, "MMM d, h:mm a")}`;
      }
      return `From ${format(startDate, "MMM d")}`;
    }

    if (endDate) {
      if (showTime) {
        return `Until ${format(endDate, "MMM d, h:mm a")}`;
      }
      return `Until ${format(endDate, "MMM d")}`;
    }

    return placeholder;
  };

  const getCustomPreviewText = (): string => {
    if (!startDate && !endDate) {
      return "Custom range...";
    }
    if (startDate && endDate) {
      return `${format(startDate, "MMM d")} → ${format(endDate, "MMM d")}`;
    }
    if (startDate) {
      return `${format(startDate, "MMM d")} → ...`;
    }
    if (endDate) {
      return `... → ${format(endDate, "MMM d")}`;
    }
    return "Custom range...";
  };

  const getStartTime = () => {
    if (!startDate) {
      return { hour: 12, minute: 0, isPM: false };
    }
    const { hour, isPM } = convertTo12Hour(startDate.getHours());
    return { hour, minute: startDate.getMinutes(), isPM };
  };

  const getEndTime = () => {
    if (!endDate) {
      return { hour: 11, minute: 59, isPM: true };
    }
    const { hour, isPM } = convertTo12Hour(endDate.getHours());
    return { hour, minute: endDate.getMinutes(), isPM };
  };

  const hasActiveFilter = value !== null && (value.start || value.end);
  const currentPreset = getCurrentPreset();
  const startTime = getStartTime();
  const endTime = getEndTime();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setViewMode("presets");
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={`flex h-8 w-auto min-w-[100px] items-center gap-1.5 rounded-md border-0 bg-transparent px-2 py-1 text-sm font-medium shadow-none transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-0 ${
            hasActiveFilter ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900"
          }`}
          aria-label={`Filter by date range${hasActiveFilter ? " (active)" : ""}`}
        >
          <CalendarIcon className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{getDisplayValue()}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[180px] border-gray-200 p-0 shadow-xl"
        align="start"
        sideOffset={4}
      >
        {/* Presets View */}
        {viewMode === "presets" && showPresets && (
          <div className="w-[180px] p-2">
            {presetOptions.map(option => (
              <Button
                key={option.value}
                onClick={() => handlePresetSelect(option.value)}
                variant="ghost"
                className={`w-full justify-start px-3 py-2 text-sm ${
                  currentPreset === option.value
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                {option.label}
                {currentPreset === option.value && <Check className="ml-auto h-4 w-4" />}
              </Button>
            ))}
            <div className="my-2 h-px bg-gray-200" />
            <Button
              onClick={handleOpenCustom}
              variant="ghost"
              className={`w-full justify-start px-3 py-2 text-sm ${
                isCustomRange
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <span className="flex-1 text-left">{getCustomPreviewText()}</span>
              {isCustomRange && <Check className="ml-auto h-4 w-4" />}
            </Button>
            {hasActiveFilter && (
              <>
                <div className="my-2 h-px bg-gray-200" />
                <Button
                  onClick={handleClear}
                  variant="ghost"
                  className="w-full justify-start px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                >
                  Clear filter
                </Button>
              </>
            )}
          </div>
        )}

        {/* Custom Start Date View */}
        {viewMode === "custom-start" && (
          <div>
            <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
              <Button
                onClick={handleBackToPresets}
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium text-gray-900">Start date</span>
            </div>
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={handleStartDateChange}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
            />
            {showTime && (
              <div className="border-t border-gray-100 bg-gray-50/30 px-4 py-3">
                <div className="mb-2.5 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs text-gray-600">Time</span>
                </div>
                <TimeInput
                  time={startTime}
                  onHourChange={hour =>
                    handleStartTimeChange(hour, startTime.minute, startTime.isPM)
                  }
                  onMinuteChange={minute =>
                    handleStartTimeChange(startTime.hour, minute, startTime.isPM)
                  }
                  onPeriodChange={isPM =>
                    handleStartTimeChange(startTime.hour, startTime.minute, isPM)
                  }
                />
              </div>
            )}
            <div className="border-t border-gray-100 px-4 py-3">
              <Button
                onClick={() => {
                  setViewMode("custom-end");
                  if (endDate) {
                    setDisplayMonth(endDate);
                  }
                }}
                variant="default"
                className="w-full text-xs"
                disabled={!startDate}
              >
                Next: Set end date →
              </Button>
            </div>
          </div>
        )}

        {/* Custom End Date View */}
        {viewMode === "custom-end" && (
          <div>
            <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
              <Button
                onClick={() => {
                  setViewMode("custom-start");
                  if (startDate) {
                    setDisplayMonth(startDate);
                  }
                }}
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium text-gray-900">End date</span>
            </div>
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={handleEndDateChange}
              disabled={date => (startDate ? date < startDate : false)}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
            />
            {showTime && (
              <div className="border-t border-gray-100 bg-gray-50/30 px-4 py-3">
                <div className="mb-2.5 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs text-gray-600">Time</span>
                </div>
                <TimeInput
                  time={endTime}
                  onHourChange={hour => handleEndTimeChange(hour, endTime.minute, endTime.isPM)}
                  onMinuteChange={minute => handleEndTimeChange(endTime.hour, minute, endTime.isPM)}
                  onPeriodChange={isPM => handleEndTimeChange(endTime.hour, endTime.minute, isPM)}
                />
              </div>
            )}
            {!isValidRange() && (
              <div className="border-t border-red-100 bg-red-50/50 px-4 py-2.5 text-xs text-red-600">
                End must be after start date
              </div>
            )}
            <div className="border-t border-gray-100 px-4 py-3">
              <Button
                onClick={() => setIsOpen(false)}
                variant="default"
                className="w-full text-xs"
                disabled={!endDate || !isValidRange()}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DateTimeRangeFilter;
